import { getMomentTz, ShiftDayNavigatorControl } from "@dexteel/mesf-core";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DateRangeIcon from "@mui/icons-material/DateRange";
import { Box, Chip, Grid2, IconButton, Tooltip } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { DatePicker } from "@mui/x-date-pickers";
import { Moment } from "moment-timezone";

import { ViewChip } from "@/pages/delays/report/components/filters/Filters";
import {
	shiftTestShackDateRange,
	TestShackDatePreset,
	TestShackMode,
} from "../../utils/period";

const moment = getMomentTz();

const DATE_PRESETS: readonly {
	value: TestShackDatePreset;
	label: string;
}[] = [
	{ value: "last7days", label: "Last 7" },
	{ value: "last30days", label: "Last 30" },
	{ value: "week", label: "Week" },
	{ value: "month", label: "Month" },
	{ value: "year", label: "Year" },
];

interface TestShackPeriodUpdate {
	mode?: TestShackMode;
	startDate?: Moment;
	endDate?: Moment;
	datePreset?: TestShackDatePreset | null;
}

export const TestShackModeChips = ({
	mode,
	datePreset,
	onChange,
}: {
	mode: TestShackMode;
	datePreset: TestShackDatePreset | null;
	onChange: (updates: TestShackPeriodUpdate) => void;
}) => {
	const isShiftMode = mode === "shift";

	return (
		<Box
			sx={{
				alignItems: "center",
				display: "flex",
				flexWrap: "wrap",
				gap: 0.75,
				mb: 1.25,
			}}
		>
			<ViewChip
				label="Shift"
				icon={<AccessTimeIcon sx={{ fontSize: 16 }} />}
				selected={isShiftMode}
				onClick={() => onChange({ mode: "shift" })}
			/>
			<ViewChip
				label="Period"
				icon={<DateRangeIcon sx={{ fontSize: 16 }} />}
				selected={!isShiftMode}
				onClick={() => onChange({ mode: "period" })}
			/>
			<Box
				sx={{ borderLeft: 1, borderColor: "divider", height: 24, mx: 0.5 }}
			/>

			{DATE_PRESETS.map((preset) => {
				const isActive = !isShiftMode && preset.value === datePreset;
				return (
					<Chip
						key={preset.value}
						label={preset.label}
						clickable={!isShiftMode}
						disabled={isShiftMode}
						aria-pressed={isActive}
						onClick={() => onChange({ datePreset: preset.value })}
						sx={{
							backgroundColor: isActive
								? (theme) => alpha(theme.palette.primary.main, 0.12)
								: "background.paper",
							border: 1,
							borderColor: isActive
								? (theme) => alpha(theme.palette.primary.main, 0.5)
								: "divider",
							borderRadius: 999,
							color: isActive ? "primary.main" : "text.secondary",
							fontSize: "0.8125rem",
							fontWeight: isActive ? 600 : 500,
							height: 30,
							px: 0.5,
							transition:
								"background-color 120ms, border-color 120ms, color 120ms",
							"&:hover": {
								backgroundColor: isActive
									? (theme) => alpha(theme.palette.primary.main, 0.18)
									: "action.hover",
								borderColor: isActive ? "primary.main" : "text.disabled",
								color: isActive ? "primary.main" : "text.primary",
							},
						}}
					/>
				);
			})}
		</Box>
	);
};

interface TestShackRangeControlsProps {
	mode: TestShackMode;
	startDate: Moment;
	endDate: Moment;
	datePreset: TestShackDatePreset | null;
	assetId?: number;
	onChange: (updates: TestShackPeriodUpdate) => void;
}

export const TestShackRangeControls = ({
	mode,
	startDate,
	endDate,
	datePreset,
	assetId,
	onChange,
}: TestShackRangeControlsProps) => {
	const canNavigateNext = endDate
		.clone()
		.startOf("day")
		.isBefore(moment().startOf("day"));

	const navigate = (direction: 1 | -1) => {
		const range = shiftTestShackDateRange(
			startDate,
			endDate,
			direction,
			datePreset,
		);
		onChange({ startDate: range.start, endDate: range.end, datePreset });
	};

	if (mode === "shift")
		return (
			<Grid2 size={{ xs: 12, md: "auto" }}>
				<Box
					sx={{
						backgroundColor: "background.paper",
						border: 1,
						borderColor: "divider",
						borderRadius: 1,
					}}
				>
					<ShiftDayNavigatorControl
						preventInit
						withPaper={false}
						assetId={assetId}
					/>
				</Box>
			</Grid2>
		);

	return (
		<>
			<Grid2 size="auto">
				<Tooltip title="Previous period">
					<IconButton
						size="small"
						aria-label="Previous period"
						onClick={() => navigate(-1)}
						sx={{ backgroundColor: "background.paper" }}
					>
						<ChevronLeftIcon fontSize="small" />
					</IconButton>
				</Tooltip>
			</Grid2>
			<Grid2 size={{ xs: 5, md: 1.4 }}>
				<DatePicker
					label="Start"
					format="MM/DD/YYYY"
					value={startDate}
					onChange={(value) => {
						if (value?.isValid())
							onChange({
								startDate: value.clone().startOf("day"),
								datePreset: null,
							});
					}}
					slotProps={{
						textField: {
							size: "small",
							fullWidth: true,
							sx: { backgroundColor: "background.paper" },
						},
					}}
				/>
			</Grid2>
			<Grid2 size={{ xs: 5, md: 1.4 }}>
				<DatePicker
					label="End"
					format="MM/DD/YYYY"
					value={endDate}
					onChange={(value) => {
						if (value?.isValid())
							onChange({
								endDate: value.clone().endOf("day"),
								datePreset: null,
							});
					}}
					slotProps={{
						textField: {
							size: "small",
							fullWidth: true,
							sx: { backgroundColor: "background.paper" },
						},
					}}
				/>
			</Grid2>
			<Grid2 size="auto">
				<Tooltip title="Next period">
					<span>
						<IconButton
							size="small"
							aria-label="Next period"
							disabled={!canNavigateNext}
							onClick={() => navigate(1)}
							sx={{ backgroundColor: "background.paper" }}
						>
							<ChevronRightIcon fontSize="small" />
						</IconButton>
					</span>
				</Tooltip>
			</Grid2>
		</>
	);
};
